<template>
    <div class="relative w-full" ref="rootRef">
        <button
            type="button"
            @click="open = !open"
            class="w-full flex items-center justify-between px-4 py-3 bg-white border-2 border-hu-green rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all shadow-sm"
            :aria-expanded="open"
            aria-haspopup="listbox"
            aria-controls="why-choose-us-lang-menu"
        >
            <span class="flex items-center gap-2">
                <span class="material-symbols-outlined text-lg text-hu-green">translate</span>
                {{ buttonLabel }}
            </span>
            <span class="material-symbols-outlined text-gray-400 transition-transform" :class="{ 'rotate-180': open }">keyboard_arrow_down</span>
        </button>
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-show="open"
                id="why-choose-us-lang-menu"
                role="listbox"
                class="language-list absolute top-full left-0 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl z-30 max-h-60 overflow-y-auto hide-scrollbar"
            >
                <div class="p-2 grid grid-cols-1 gap-1">
                    <a
                        v-for="lang in visibleLanguages"
                        :key="lang.code"
                        :href="pathsByLanguage[lang.code] || '#'"
                        class="flex items-center gap-3 px-3 py-2 hover:bg-pale-blue rounded-lg text-sm font-medium text-gray-700 cursor-pointer transition-colors"
                    >
                        <img
                            v-if="lang.flag"
                            :src="lang.flag"
                            alt=""
                            class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0"
                            width="20"
                            height="20"
                        />
                        <span v-else class="text-base">{{ flagEmoji(lang.code) }}</span>
                        <span>{{ lang.translated?.name ?? lang.code_view ?? lang.code }}</span>
                    </a>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

export default {
    name: 'WhyChooseUsLanguageDropdown',
    setup() {
        const store = useStore();
        const open = ref(false);
        const rootRef = ref(null);

        const pathsByLanguage = computed(() => store.state.page?.paths_by_language || {});
        const languages = computed(() => store.getters.languages || []);
        const texts = computed(() => store.getters.texts || {});

        const visibleLanguages = computed(() => {
            const paths = pathsByLanguage.value;
            return Object.keys(paths)
                .map((code) => languages.value.find((l) => l.code === code))
                .filter(Boolean);
        });

        const buttonLabel = computed(() => {
            const n = visibleLanguages.value.length;
            const key = 'why-choose-us.blade.php-4-card-1-button';
            const fromTexts = texts.value[key];
            if (fromTexts) return fromTexts;
            return n ? `View all ${n} languages` : 'View all languages';
        });

        function flagEmoji(code) {
            if (!code || code.length !== 2) return '🌐';
            const c1 = code.charCodeAt(0);
            const c2 = code.charCodeAt(1);
            const u1 = 0x1F1E6 + (c1 >= 97 ? c1 - 97 : c1 - 65);
            const u2 = 0x1F1E6 + (c2 >= 97 ? c2 - 97 : c2 - 65);
            return String.fromCodePoint(u1, u2);
        }

        function onOutsideClick(e) {
            if (rootRef.value && !rootRef.value.contains(e.target)) {
                open.value = false;
            }
        }

        onMounted(() => {
            document.addEventListener('click', onOutsideClick);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', onOutsideClick);
        });

        return { open, rootRef, visibleLanguages, pathsByLanguage, buttonLabel, flagEmoji };
    },
};
</script>

<style scoped>
.rotate-180 {
    transform: rotate(180deg);
}
.hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
    display: none;
}
</style>
